import { createLucideIcon, type LucideIcon } from "lucide-react";

/* The lean symbol set — glyphs the icon library we use does not ship.
 *
 * WHY THIS FILE EXISTS. lucide has ~1 862 icons and not one of them is a lean
 * symbol: no A3, no PDCA wheel, no kaizen burst, no heijunka box, no
 * supermarket, no FIFO lane, no andon, no maturity staircase, no X-matrix and
 * no bowler chart. A lean transformation platform that picks its marks from a
 * general-purpose catalogue therefore always lands on a near-miss, and the
 * near-misses were not neutral: the value stream wore the Git branch glyph,
 * strategy deployment wore the OKR bullseye, and daily management wore the
 * devops uptime trace. Those are three different products' iconography.
 *
 * HOW THEY ARE BUILT. `createLucideIcon` returns exactly `LucideIcon`, which is
 * the type all four module registries require (`ModuleManifest.icon`,
 * `AppLauncherEntry.icon`, `Tab.icon`, `SettingsModule.icon`), so these drop in
 * with no change to a single contract. They also inherit lucide's own wrapper:
 * the 24x24 viewBox, `stroke-width` 2, round cap and join, and the `size` /
 * `strokeWidth` / `color` props — which is what keeps a hand-drawn mark from
 * reading visibly lighter or heavier than the lucide marks beside it.
 *
 * NOT THE SAME THING as `vsm-shape-catalog.tsx`. That file draws the 18 Rother
 * & Shook symbols a user PLACES ON THE VSM CANVAS: plain SVG components on a
 * 72x56 viewBox at stroke 1.8, sized by their node. These are CHROME marks —
 * square, 16-20px, and typed `LucideIcon`. The two cannot merge, and neither
 * should replace the other.
 *
 * NAMED FOR WHAT THEY DEPICT, NEVER FOR A MODULE. `A3Sheet`, not `SolveIcon`.
 * The design system does not know which module uses which mark; the shell's
 * `MODULE_PRESENTATION` registry decides that, the same way it decides module
 * colour under ADR-0092. A future module, feature or surface adds its glyph
 * here and maps it there.
 *
 * ADDING ONE. Draw on a 24x24 grid, keep to 2-5 primitives, and let no feature
 * be narrower than about 4 grid units — lucide ships no optical sizes, so the
 * stroke thins to ~1.33px at 16px and anything finer closes up or disappears.
 * Never fill: a filled mark in a row of outlines reads as "selected". Then look
 * at it at 16px in both themes before believing it works.
 */

/** Structured one-page problem solving: the A3 sheet, Plan on the left,
 *  Do-Check-Act on the right — the layout the method is named after. */
export const A3Sheet: LucideIcon = createLucideIcon("A3Sheet", [
  [
    "rect",
    { x: "2.5", y: "4.5", width: "19", height: "15", rx: "2", key: "sheet" },
  ],
  ["path", { d: "M11 4.5v15", key: "fold" }],
  ["path", { d: "M5.2 9h3.4M5.2 13h3.4", key: "plan" }],
  ["path", { d: "M13.8 10.5h4.4M13.8 14.5h4.4", key: "do" }],
]);

/** A value stream: two process boxes with material moving between them, over
 *  the sawtooth lead-time ladder that runs beneath every value stream map.
 *  The ladder is the most VSM-specific shape there is — nothing else in
 *  business software looks like it. */
export const ProcessChain: LucideIcon = createLucideIcon("ProcessChain", [
  [
    "rect",
    { x: "2.5", y: "3.5", width: "7", height: "5.5", rx: "1", key: "a" },
  ],
  [
    "rect",
    { x: "14.5", y: "3.5", width: "7", height: "5.5", rx: "1", key: "b" },
  ],
  ["path", { d: "M10 6.25h3.2", key: "flow" }],
  ["path", { d: "M12 5.05 13.4 6.25 12 7.45", key: "head" }],
  ["path", { d: "M2.5 19.5h3.6V14.8h5.2v4.7h5.2v-4.7h5", key: "ladder" }],
]);

/** The tier board on its stand — the physical object a daily huddle forms
 *  around, carrying the day's numbers. */
export const TierBoard: LucideIcon = createLucideIcon("TierBoard", [
  [
    "rect",
    { x: "2.5", y: "2.5", width: "19", height: "13.5", rx: "2", key: "board" },
  ],
  ["path", { d: "M7.5 12.5V9M12 12.5V6.5M16.5 12.5v-2", key: "bars" }],
  ["path", { d: "m8.5 21.5 3.5-5.5 3.5 5.5", key: "stand" }],
]);

/** The kaizen burst: the starburst that marks an improvement opportunity on a
 *  value stream map, and the one symbol the method has of its own.
 *
 *  An 8-point star, deliberately matching the burst
 *  `vsm-feature/src/canvas/vsm-shape-catalog.tsx` already draws as the canvas
 *  KAIZEN shape, so the same idea wears the same mark whether it is a symbol a
 *  facilitator drops on a map or the module's own entry in the launcher.
 *
 *  The spikes are deep on purpose (inner radius 4.7 of an outer 9.6). A
 *  shallower burst rounds into a flower at 16px and loses the energy that makes
 *  a burst read as a burst; a 6-point version reads as a favourite star. */
export const KaizenBurst: LucideIcon = createLucideIcon("KaizenBurst", [
  [
    "path",
    {
      d: "M12 2.4 13.8 7.66 18.79 5.21 16.34 10.2 21.6 12 16.34 13.8 18.79 18.79 13.8 16.34 12 21.6 10.2 16.34 5.21 18.79 7.66 13.8 2.4 12 7.66 10.2 5.21 5.21 10.2 7.66Z",
      key: "burst",
    },
  ],
]);

/** The five-axis spider plot a maturity assessment is scored and reported on:
 *  the outer web is the ceiling, the inner polygon is where you actually are. */
export const MaturityWeb: LucideIcon = createLucideIcon("MaturityWeb", [
  ["path", { d: "M12 2.5 21.04 9.06 17.58 19.69H6.42L2.96 9.06Z", key: "web" }],
  [
    "path",
    { d: "M12 6.4 16.6 10.3 15.3 16.4 9.9 14.6 7.4 10.2Z", key: "score" },
  ],
]);
